import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaWhatsapp,
  FaClock,
  FaLinkedin,
  FaFacebook,
  FaInstagram,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ContactUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const [formType, setFormType] = useState("it");

  const [itData, setItData] = useState({
    form_type: "it",
    fullname: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    description: "",
    deadline: "",
    file: null,
  });

  const [dmData, setDmData] = useState({
    form_type: "dm",
    fullname: "",
    brand: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    goals: [],
    description: "",
    file: null,
  });

  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, duration: 0.8 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  // Service options mapped to backend values
  const itServices = [
    { value: "web_development", label: "Web Development" },
    { value: "app_development", label: "App Development" },
    { value: "software_development", label: "Software Development" },
    { value: "cloud_solutions", label: "Cloud Solutions" },
    { value: "it_consulting", label: "IT Consulting" },
    { value: "automation_ai", label: "Automation/AI Integration" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "other_it", label: "Other" },
  ];

  const dmServices = [
    { value: "social_media_marketing", label: "Social Media Marketing" },
    { value: "performance_marketing", label: "Performance Marketing (Ads)" },
    { value: "seo", label: "SEO" },
    { value: "branding_design", label: "Branding & Creative Design" },
    { value: "content_marketing", label: "Content Marketing" },
    { value: "lead_generation", label: "Lead Generation" },
    { value: "website_marketing", label: "Website Marketing" },
    { value: "marketing_automation", label: "Marketing Automation" },
    { value: "all_in_one", label: "All-in-One Digital Marketing" },
    { value: "other_dm", label: "Other" },
  ];

  // Budget options mapped to backend values
  const itBudgets = [
    { value: "it_25_50k", label: "₹25,000 – ₹50,000" },
    { value: "it_50_100k", label: "₹50,000 – ₹1,00,000" },
    { value: "it_100_500k", label: "₹1,00,000 – ₹5,00,000" },
    { value: "it_500k_plus", label: "₹5,00,000+" },
  ];

  const dmBudgets = [
    { value: "dm_20_50k", label: "₹20,000 – ₹50,000" },
    { value: "dm_50_100k", label: "₹50,000 – ₹1,00,000" },
    { value: "dm_100_500k", label: "₹1,00,000 – ₹5,00,000" },
    { value: "dm_500k_plus", label: "₹5,00,000+" },
  ];

  // Business goals mapped to backend values
  const businessGoals = [
    { value: "increase_leads", label: "Increase Leads" },
    { value: "brand_awareness", label: "Improve Brand Awareness" },
    { value: "social_media_growth", label: "Grow Social Media" },
    { value: "website_traffic", label: "Increase Website Traffic" },
    { value: "scale_sales", label: "Scale Sales" },
  ];

  const handleITChange = (e) => {
    const { name, value, files } = e.target;
    setItData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleDMChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (type === "checkbox") {
      setDmData((prev) => {
        const newGoals = checked
          ? [...prev.goals, value]
          : prev.goals.filter((g) => g !== value);
        return { ...prev, goals: newGoals };
      });
      return;
    }

    setDmData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleITSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add all IT form fields
      Object.keys(itData).forEach(key => {
        if (key === 'file' && itData[key]) {
          formData.append(key, itData[key]);
        } else if (itData[key] !== null && itData[key] !== '') {
          formData.append(key, itData[key]);
        }
      });

      const response = await fetch(
        "https://amplinova.pythonanywhere.com/api/contact/",
        {
          method: "POST",
          body: formData,
          // Don't set Content-Type header for FormData - browser will set it automatically with boundary
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        // Reset form
        setItData({
          form_type: "it",
          fullname: "",
          company: "",
          email: "",
          phone: "",
          service: "",
          budget: "",
          description: "",
          deadline: "",
          file: null,
        });
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        const errorData = await response.json();
        console.error('Submission error:', errorData);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error('Network error:', err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDMSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add all DM form fields
      Object.keys(dmData).forEach(key => {
        if (key === 'file' && dmData[key]) {
          formData.append(key, dmData[key]);
        } else if (key === 'goals') {
          // Handle goals array
          dmData.goals.forEach(goal => {
            formData.append('goals', goal);
          });
        } else if (dmData[key] !== null && dmData[key] !== '') {
          formData.append(key, dmData[key]);
        }
      });

      const response = await fetch(
        "https://amplinova.pythonanywhere.com/api/contact/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        // Reset form
        setDmData({
          form_type: "dm",
          fullname: "",
          brand: "",
          email: "",
          phone: "",
          service: "",
          budget: "",
          goals: [],
          description: "",
          file: null,
        });
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        const errorData = await response.json();
        console.error('Submission error:', errorData);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error('Network error:', err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: FaPhone,
      title: "Call Us",
      details: "+91 9976089089",
      description: "Mon–Sat, 11AM – 8PM",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: FaEnvelope,
      title: "Email Us",
      details: "info@amplinova.com",
      description: "We reply within 24 hours",
      color: "from-amber-500 to-orange-500",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Visit Us",
      details: "Hyderabad, Telangana",
      description: "India",
      color: "from-emerald-500 to-green-500",
    },
    {
      icon: FaClock,
      title: "Business Hours",
      details: "Mon – Fri",
      description: "11:00 AM – 8:00 PM IST",
      color: "from-purple-500 to-pink-500",
    },
  ];

  const socialLinks = [
    { icon: FaLinkedin, url: "https://www.linkedin.com/company/109608290/admin/dashboard/", color: "hover:text-blue-600" },
    { icon: FaWhatsapp, url: "https://wa.me/919976089089", color: "hover:text-cyan-500" },
    { icon: FaFacebook, url: "https://www.facebook.com/people/Amplinova-Solutions/61583932147457/", color: "hover:text-blue-500" },
    { icon: FaInstagram, url: "https://www.instagram.com/amplinova_solutions?igsh=MWliem1pcnRsb2syNw==", color: "hover:text-pink-500" },
  ];

  return (
  <>
    <Navbar />

    <section className="pt-10 pb-10 bg-gradient-to-br from-amber-50 via-white to-orange-50 text-gray-800 overflow-hidden relative">
      <div className="container mx-auto px-6 relative z-10">

        {/* HEADER */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 bg-white/80 border border-amber-100 rounded-full px-6 py-3 mb-6 shadow-sm"
          >
            <FaPaperPlane className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-700 uppercase">
              Get In Touch
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-5xl font-bold mb-6">
            Contact{" "}
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Us
            </span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Choose your service type and fill the related form.
          </motion.p>
        </motion.div>

        {/* ⭐ TWO COLUMN LAYOUT — EXACT LIKE REFERENCE ⭐ */}
        <div className="grid grid-cols-1 lg:grid-cols-[25%_75%] gap-10 md:gap-6 sm:gap-4">
  {/* LEFT SIDE — SMALLER, STICKY */}
  <motion.div
    variants={itemVariants}
    className="space-y-4 lg:sticky lg:top-32 lg:self-start"
  >
    {contactMethods.map((method, i) => (
      <motion.div
        key={i}
        variants={itemVariants}
        className="bg-white p-4 rounded-xl border border-amber-100 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 bg-gradient-to-r ${method.color} rounded-lg flex items-center justify-center shadow`}
          >
            <method.icon className="w-5 h-5 text-white" />
          </div>

          <div>
            <h3 className="text-lg font-bold">{method.title}</h3>
            <p className="text-sm font-semibold">{method.details}</p>
            <p className="text-gray-600 text-xs">{method.description}</p>
          </div>
        </div>
      </motion.div>
    ))}

    {/* Social Icons */}
    <div className="mt-4">
      <h3 className="text-lg font-bold mb-3">Follow Us</h3>
      <div className="flex gap-3">
        {socialLinks.map((s, i) => (
          <a
            key={i}
            href={s.url}
            target="_blank"
            rel="noreferrer"
            className={`w-10 h-10 bg-white rounded-lg border border-amber-100 flex items-center justify-center shadow ${s.color}`}
          >
            <s.icon className="w-5 h-5 text-gray-600" />
          </a>
        ))}
      </div>
    </div>
  </motion.div>


          {/* RIGHT — FORM BOX (MATCHING REFERENCE UI EXACTLY) */}
          <motion.div variants={itemVariants}>
            <div className="bg-white p-8 rounded-2xl border border-amber-100 shadow-sm">

              {/* Tabs */}
              <div className="flex w-full mb-8">
                <button
                  type="button"
                  onClick={() => setFormType("it")}
                  className={`w-1/2 py-3 font-semibold rounded-l-xl border text-center ${
                    formType === "it"
                      ? "bg-amber-500 text-white border-amber-600"
                      : "bg-white text-gray-600 border-gray-300"
                  }`}
                >
                  IT Services
                </button>

                <button
                  type="button"
                  onClick={() => setFormType("dm")}
                  className={`w-1/2 py-3 font-semibold rounded-r-xl border ${
                    formType === "dm"
                      ? "bg-amber-500 text-white border-amber-600"
                      : "bg-white text-gray-600 border-gray-300"
                  }`}
                >
                  Digital Marketing
                </button>
              </div>

              {/* SUCCESS / ERROR */}
              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
                  Thank you! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                  Something went wrong. Try again.
                </div>
              )}

              {/* IT FORM */}
{formType === "it" && (
  <form onSubmit={handleITSubmit} className="space-y-6">
    <h3 className="text-2xl font-bold mb-4">
      IT Development & Tech Solutions
    </h3>

    {/* Full Name + Company */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
        <input
          type="text"
          name="fullname"
          placeholder="Enter your Full Name"
          value={itData.fullname}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
        <input
          type="text"
          name="company"
          placeholder="Enter your Company Name"
          value={itData.company}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        />
      </div>
    </div>

    {/* Email + Phone */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
        <input
          type="email"
          name="email"
          placeholder="Enter your Email"
          value={itData.email}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
        <input
          type="tel"
          name="phone"
          placeholder="Enter your Phone Number"
          value={itData.phone}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        />
      </div>
    </div>

    {/* Service + Budget */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Service Required *</label>
        <select
          name="service"
          value={itData.service}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        >
          <option value="">Select Service</option>
          {itServices.map((service) => (
            <option key={service.value} value={service.value}>
              {service.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Project Budget *</label>
        <select
          name="budget"
          value={itData.budget}
          onChange={handleITChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        >
          <option value="">Select Budget</option>
          {itBudgets.map((budget) => (
            <option key={budget.value} value={budget.value}>
              {budget.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Description */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Project Description *</label>
      <textarea
        rows="4"
        name="description"
        placeholder="Enter details about your project requirements..."
        value={itData.description}
        onChange={handleITChange}
        className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        required
      ></textarea>
    </div>

    {/* Submit */}
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full py-3 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition"
    >
      {isSubmitting ? "Submitting..." : "Request Proposal"}
    </button>
  </form>
)}
{/* DIGITAL MARKETING FORM */}
{formType === "dm" && (
  <form onSubmit={handleDMSubmit} className="space-y-6">

    <h3 className="text-2xl font-bold mb-4">
      Digital Marketing, Branding & Growth Solutions
    </h3>

    {/* Full Name + Business Name */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
        <input
          type="text"
          name="fullname"
          placeholder="Enter your Full Name"
          value={dmData.fullname}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
        <input
          type="text"
          name="brand"
          placeholder="Enter your Business Name"
          value={dmData.brand}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        />
      </div>
    </div>

    {/* Email + Phone */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
        <input
          type="email"
          name="email"
          placeholder="Enter your Email"
          value={dmData.email}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
        <input
          type="tel"
          name="phone"
          placeholder="Enter your Phone Number"
          value={dmData.phone}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        />
      </div>
    </div>

    {/* Service + Budget */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In *</label>
        <select
          name="service"
          value={dmData.service}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        >
          <option value="">Select Service</option>
          {dmServices.map((service) => (
            <option key={service.value} value={service.value}>
              {service.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Budget *</label>
        <select
          name="budget"
          value={dmData.budget}
          onChange={handleDMChange}
          className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
          required
        >
          <option value="">Select Budget</option>
          {dmBudgets.map((budget) => (
            <option key={budget.value} value={budget.value}>
              {budget.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Business Goals (2-column grid) */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Business Goals *</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {businessGoals.map((goal) => (
          <label
            key={goal.value}
            className="flex items-center gap-3 p-3 border rounded-lg hover:bg-orange-50 cursor-pointer"
          >
            <input
              type="checkbox"
              name="goals"
              value={goal.value}
              checked={dmData.goals.includes(goal.value)}
              onChange={handleDMChange}
              className="w-4 h-4 text-amber-600"
            />
            <span>{goal.label}</span>
          </label>
        ))}
      </div>
    </div>

    {/* Description */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Describe Your Needs *</label>
      <textarea
        rows="4"
        name="description"
        placeholder="Tell us about your goals..."
        value={dmData.description}
        onChange={handleDMChange}
        className="w-full px-4 py-3 border rounded-lg border-gray-300 focus:ring-2 focus:ring-amber-500"
        required
      ></textarea>
    </div>

    {/* Submit */}
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full py-3 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition"
    >
      {isSubmitting ? "Submitting..." : "Get Marketing Strategy"}
    </button>
  </form>
)}
            </div>
          </motion.div>
        </div>

        {/* Emergency Box */}
        <motion.div
          variants={itemVariants}
          className="mt-16 bg-gradient-to-r from-amber-500 to-orange-500 text-white p-10 rounded-3xl text-center shadow-lg"
        >
          <h4 className="text-2xl font-bold mb-4">Emergency Support</h4>
          <p className="text-amber-100 mb-4">Need immediate assistance?</p>
          <p className="text-3xl font-extrabold">+91 9976089089</p>
          <p className="text-sm text-amber-100">24/7 Technical Support</p>
        </motion.div>
      </div>
    </section>

    <Footer />
  </>
);
};

export default ContactUs;